import { useCallback, useMemo, useState } from "react";
import type { NurseData } from "../../../utils/models";
import type { NursesMatrixSelectionMode } from "../../NursesMatrixGrid/consts";
import type { RemoveStaffRow } from "./removeStaffRows";

/** The picks, remembered per date: a pick on another date is a different shift, so it never carries over. */
type DatedPicks = { dateKey: string; shiftIds: string[] };

/**
 * The next pick list after the grid reports what is picked among the rows it SHOWS. The grid only sees the
 * filtered window, so picks in other windows stay; picks it shows are kept or dropped as it says; new ones
 * are appended, keeping pick order. Single mode holds exactly the grid's one pick.
 */
export const mergeVisiblePicks = (
	mode: NursesMatrixSelectionMode,
	current: readonly string[],
	visibleShiftIds: ReadonlySet<string>,
	pickedVisible: readonly string[],
): string[] => {
	if (mode === "single") return [...pickedVisible];
	const pickedSet = new Set(pickedVisible);
	const kept = current.filter((shiftId) => !visibleShiftIds.has(shiftId) || pickedSet.has(shiftId));
	const added = pickedVisible.filter((shiftId) => !current.includes(shiftId));
	return [...kept, ...added];
};

const shiftIdOf = (row: NurseData): string => {
	// The grid only ever reports rows it was given, and every Remove Staff row carries its shift id.
	if (!row.shiftId) throw new Error("Remove Staff grid reported a row without a shift id");
	return row.shiftId;
};

type Args = {
	mode: NursesMatrixSelectionMode;
	/** Every row on the date (the filter's hidden ones included). */
	rows: readonly RemoveStaffRow[];
	/** The rows the grid shows under the active filter. */
	visibleRows: readonly RemoveStaffRow[];
	dateKey: string;
};

/**
 * The shifts picked for removal, owned here and echoed to the grid. Picks survive a filter change — the pills
 * narrow what is SHOWN, not what is picked — and are dropped by a date change and by the list itself (a shift
 * no longer on the date is never kept).
 */
export const useRemoveStaffSelection = ({ mode, rows, visibleRows, dateKey }: Args) => {
	const [picks, setPicks] = useState<DatedPicks>({ dateKey, shiftIds: [] });
	const currentIds = useMemo(() => (picks.dateKey === dateKey ? picks.shiftIds : []), [picks, dateKey]);
	const rowById = useMemo(() => new Map(rows.map((row) => [row.shiftId, row])), [rows]);
	const selectedRows = useMemo(() => currentIds.map((id) => rowById.get(id)).filter((row): row is RemoveStaffRow => row !== undefined), [currentIds, rowById]);
	const visibleIds = useMemo(() => new Set(visibleRows.map((row) => row.shiftId)), [visibleRows]);
	const gridSelectedIds = useMemo(() => selectedRows.map((row) => row.shiftId).filter((id) => visibleIds.has(id)), [selectedRows, visibleIds]);

	const onGridSelectionChange = useCallback(
		(picked: NurseData[]) =>
			setPicks({
				dateKey,
				shiftIds: mergeVisiblePicks(
					mode,
					selectedRows.map((row) => row.shiftId),
					visibleIds,
					picked.map(shiftIdOf),
				),
			}),
		[dateKey, mode, selectedRows, visibleIds],
	);

	/** Replaces the picks outright — e.g. with the shifts a partly failed removal couldn't remove. */
	const replaceSelection = useCallback((shiftIds: string[]) => setPicks({ dateKey, shiftIds }), [dateKey]);

	return { selectedRows, gridSelectedIds, onGridSelectionChange, replaceSelection };
};
